<template>
  <q-card flat class="q-pa-md">
    <div class="text-h6 q-py-xs" :class="$q.dark.mode ? '' : 'text-primary'">
      Live Probable Circuit List<span class="text-caption">
        ( Its a probable circuit list )</span
      >
      <div class="text-caption text-bold q-pa-xs">
        As of : {{ convertDate(store.state.market.asOfLive) }}
      </div>
    </div>
    <PackageAuth>
      <template #authorized-content>
        <q-table
          flat
          bordered
          :rows="finalComputedData"
          :columns="columns"
          :pagination="pagination"
          separator="cell"
          binary-state-sort
          :filter="filterSymbol"
          :filter-method="(rows) => filterSymbolData(rows, filterSymbol)"
          :class="
            $q.dark.mode === true
              ? 'table-sticky-header-column-dark'
              : 'table-sticky-header-column'
          "
        >
          <template v-slot:top-left>
            <q-select
              outline
              v-model="selectedSector"
              :options="optionSectors"
              dense
              label="Choose sector"
              single
              input-debounce="0"
              option-label="name"
              option-value="symbol"
              class="ellipsis q-mr-sm"
              :menu-absolute="true"
              style="width: 120px"
            />

            <StockSearch
              :allStocks="allStocks"
              @emitededSymbol="
                (eSymbol) => {
                  filterSymbol = eSymbol;
                }
              "
              style="width: 120px"
            />

            <q-checkbox
              dense
              class="q-pl-sm ellipsis"
              v-model="mySignals"
              color="blue-5"
              keep-color
              label="My Signals"
              style="width: 120px"
            >
              <q-tooltip class="bg-primary">My Signals Only</q-tooltip>
            </q-checkbox>
          </template>

          <template v-slot:body-cell-symbol="props">
            <q-td :props="props">
              <q-item dense clickable @click="onClickSymbolEmit(props.value)">
                <div class="col">
                  {{ props.value }}
                  <q-tooltip>{{
                    allStocks.find((stock) => stock.symbol == props.value)?.name
                  }}</q-tooltip>
                </div>
              </q-item>
            </q-td>
          </template>
        </q-table>
      </template>
    </PackageAuth>
  </q-card>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";

import useData from "src/use/data";
import { useStore } from "vuex";
import moment from "moment";
import PackageAuth from "src/components/Packages/PackageAuth.vue";
import useUtils from "src/use/utils";
import { LocalStorage, useQuasar } from "quasar";
import StockSearch from "src/components/StockProfile/StockSearch";

const $q = useQuasar();
const store = useStore();
const emit = defineEmits(["chartSymbol"]);

const { CircuitSetup, ActiveScriptLive, BulkTransactionWithMarketBuySell } =
  useData();

const { filterSymbolData, utilsOptionSectors } = useUtils();
const filterSymbol = ref("");

const isMarketOn = ref(false);
const allStocks = ref([]);
const mySignals = ref(false);
const mySignalsStocks = ref([]);

const circuitSetupData = ref([]);
const activeScriptLiveData = ref([]);
const bulkTransactionWithMarketData = ref([]);

const optionSectors = ref(utilsOptionSectors());
const selectedSector = ref(optionSectors.value[0]);

const groupDataBySymbol = (data) =>
  data.reduce((acc, item) => {
    if (!acc[item.symbol]) {
      acc[item.symbol] = [];
    }
    acc[item.symbol].push(item);
    return acc;
  }, {});

const filterMktBulkTransaction = (data) => {
  const groupedData = groupDataBySymbol(data);
  const filteredSymbols = Object.entries(groupedData)
    .filter(([symbol, items]) => {
      const totalValue = items.reduce(
        (sum, item) => sum + item.cumm_quantity * item.rate,
        0
      );
      const hasSignificantChange = items.filter(
        (item) => item.cng_value / item.f_rate >= 0.05
      );

      return (
        (items.length > 2 && totalValue > 5000000) ||
        (hasSignificantChange && totalValue > 3000000)
      );
    })
    .map(([symbol]) => symbol);

  return data.filter((item) => filteredSymbols.includes(item.symbol));
};

async function loadLiveData() {
  $q.loading.show({});
  try {
    const [
      resCircuitData,
      // resActiveScriptLive,
      resBulkTransactionWithMarket,
    ] = await Promise.all([
      CircuitSetup(),
      // ActiveScriptLive(30),
      BulkTransactionWithMarketBuySell(),
    ]);

    circuitSetupData.value = resCircuitData.data.sort(
      (a, b) => a.high_close - b.high_close
    );
    // activeScriptLiveData.value = resActiveScriptLive.data.filter(
    //   (i) => i.rn <= 30
    // );
    bulkTransactionWithMarketData.value = filterMktBulkTransaction(
      resBulkTransactionWithMarket.data.filter((i) => i.bulk_status === "BUY")
    );
  } catch (error) {
    console.error("Error loading live data:", error);
  } finally {
    $q.loading.hide();
  }
}

const computedCircuitSetupData = computed(() => {
  const sectorId = selectedSector.value.id;
  const sectorSymbols = new Set(
    allStocks.value
      .filter((i) => i.sector === sectorId)
      .map((stock) => stock.symbol)
  );

  // const activeScriptSymbols = new Set(
  //   activeScriptLiveData.value.map((entry) => entry.symbol)
  // );

  const bulkTransactionSymbols = new Set(
    bulkTransactionWithMarketData.value.map((entry) => entry.symbol)
  );

  const latestEntries = circuitSetupData.value.reduce((acc, entry) => {
    const currentEntry = acc.get(entry.symbol);
    if (!currentEntry || new Date(entry.date) > new Date(currentEntry.date)) {
      acc.set(entry.symbol, entry);
    }
    return acc;
  }, new Map());

  const latestEntriesArray = Array.from(latestEntries.values());

  // const repeatedGroupData = groupDataBySymbol(circuitSetupData.value);
  // const repeatedCircuitSetupData = Object.entries(repeatedGroupData)
  //   .filter(([symbol, items]) => items.length > 4)
  //   .map(([symbol]) => symbol);

  // const finaldata = circuitSetupData.value.filter((item) =>
  //   repeatedCircuitSetupData.includes(item.symbol)
  // );

  const groupAndFilterData = (data) => {
    const symbolMap = new Map();
    data.forEach((item) => {
      if (!symbolMap.has(item.symbol)) {
        symbolMap.set(item.symbol, { count: 0, items: [] });
      }
      const symbolData = symbolMap.get(item.symbol);
      symbolData.count += 1;
      symbolData.items.push(item);
    });

    // const symbolMap = data.reduce((acc, item) => {
    //   if (!acc.has(item.symbol)) {
    //     acc.set(item.symbol, { count: 0, items: [] });
    //   }
    //   const symbolData = acc.get(item.symbol);
    //   symbolData.count += 1;
    //   symbolData.items.push(item);
    //   return acc;
    // }, new Map());

    const finalData = [];
    symbolMap.forEach(({ count, items }) => {
      if (count > 5) {
        finalData.push(...items);
      }
    });

    return finalData;
  };

  const repeatedCktData = new Set(
    groupAndFilterData(circuitSetupData.value).map((item) => item.symbol)
  );

  const applyFilters = (data) =>
    data.filter((item) => {
      const isSectorMatch = sectorId === 0 || sectorSymbols.has(item.symbol);

      const probableCircuitListData =
        // activeScriptSymbols.has(item.symbol) &&
        repeatedCktData.has(item.symbol) ||
        bulkTransactionSymbols.has(item.symbol);

      return isSectorMatch && probableCircuitListData;
    });

  return applyFilters(latestEntriesArray);
});

const finalComputedData = computed(() => {
  return computedCircuitSetupData.value.filter((item) => {
    const isMySignalMatch =
      !mySignals.value ||
      mySignalsStocks.value.some((stock) => stock === item.symbol);

    return isMySignalMatch;
  });
});

onMounted(async () => {
  await loadLiveData();

  if (!isMarketOn.value) {
    await store.dispatch("market/marketStatusNew");
    isMarketOn.value = store.state.market.marketStatus;
  }
  const currentPackage = store.state.packages.packageStatus;
  if (currentPackage.current_package.stocks.length) {
    mySignalsStocks.value = currentPackage.current_package.stocks;
  } else {
    await store.dispatch("packages/getPackageStatus");
    mySignalsStocks.value = currentPackage.current_package.stocks;
  }

  allStocks.value = store.state.core.allStocks || [];
  if (!allStocks.value.length) {
    await store.dispatch("core/getAllStocks");
    allStocks.value = store.state.core.allStocks;
  }
});

const columns = [
  {
    name: "date",
    label: "Circuit Setup time",
    field: "date",
    format: (val, row) => {
      return convertDate(val);
    },
    sortable: true,
    align: "left",
  },
  {
    name: "symbol",
    label: "Symbol",
    field: "symbol",
    sortable: true,
    align: "center",
  },
  {
    name: "close",
    label: "LTP",
    field: "close",
    sortable: true,
    align: "center",
  },
  {
    name: "open",
    label: "Open",
    field: "open",
    sortable: true,
    align: "center",
  },
  {
    name: "high",
    label: "High",
    field: "high",
    sortable: true,
    align: "center",
  },
  {
    name: "low",
    label: "Low",
    field: "low",
    sortable: true,
    align: "center",
  },
  {
    name: "high_close",
    label: "High - Ltp %",
    field: "high_close",
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    sortable: true,
    align: "center",
  },
  {
    name: "high_open",
    label: "High - Open %",
    field: "high_open",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "status",
    label: "Circuit Status",
    field: "status",
    sortable: true,
    format: (val, row) => {
      const status = ((row.high - row.close) / row.close) * 100;
      return isMarketOn.value
        ? "InProgress"
        : status < 1.5
        ? "Completed"
        : "Incompleted";
    },
    style: (row) => {
      const status = ((row.high - row.close) / row.close) * 100;
      return isMarketOn.value
        ? "font-weight: bold;"
        : status < 1.5
        ? "color:green; font-weight: bold;"
        : "color:red; font-weight: bold;";
    },

    align: "center",
  },
];

const onClickSymbolEmit = (symbol) => {
  emit("chartSymbol", symbol);
};

const pagination = {
  sortBy: "date",
  rowsPerPage: 0,
  descending: true,
};

const convertDate = (date) => moment(date).format("YYYY-MM-DD, hh:mm A");
</script>
